<script setup lang="ts">
import { ref } from 'vue'
import UiFieldContainer from './UiFieldContainer.vue'

export interface UiNumberFieldProps {
  placeholder?: string
  name?: string
  id?: string
  class?: string | string[] | Record<string, boolean>
  required?: boolean
  readonly?: boolean
  disabled?: boolean
  min?: number
  max?: number
  step?: number
  size?: 'md' | 'lg'
  hint?: string
  error?: string
  invalid?: boolean
  ariaLabel?: string
}

const props = withDefaults(defineProps<UiNumberFieldProps>(), {
  class: '',
  required: false,
  readonly: false,
  disabled: false,
  size: 'md',
})

const model = defineModel<string>()
const inputRef = ref<HTMLInputElement>()
const isFocused = ref(false)

const emit = defineEmits<{
  (e: 'validate'): void
}>()

function handleInput(event: Event) {
  const target = event.target as HTMLInputElement
  model.value = target.value
}

function focusInput() {
  isFocused.value = true
  inputRef.value?.focus()
}

function blurInput() {
  isFocused.value = false
  inputRef.value?.blur()
  emit('validate')
}
</script>

<template>
  <UiFieldContainer
    :class="props.class"
    :isFocused="isFocused"
    :size="props.size"
    :invalid="props.invalid"
    :hint="props.hint"
    :error="props.error"
    :describedByIdBase="props.id || props.name"
    :disabled="props.disabled"
    gap="0"
    @focus="focusInput"
    @blur="blurInput"
  >
    <input
      ref="inputRef"
      :placeholder="props.placeholder"
      :name="props.name"
      :id="props.id || props.name"
      :value="model"
      class="text-md min-w-0 flex-1 se-font-input font-archivo"
      type="number"
      :required="props.required"
      :readonly="props.readonly"
      :disabled="props.disabled"
      :min="props.min"
      :max="props.max"
      :step="props.step"
      :aria-invalid="props.invalid ? 'true' : undefined"
      :aria-label="props.ariaLabel"
      :aria-describedby="props.error ? (props.id || props.name) + '-error' : (props.hint ? (props.id || props.name) + '-hint' : undefined)"
      @input="handleInput"
      @blur="blurInput"
    />
  </UiFieldContainer>
</template>

<style scoped>
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
  appearance: textfield;
}
</style>
